import { PermitRateCouponOutput } from '@parkingbase/ecommerce-api-demo';
import type { GaragePermitRate } from '@/types/reserve.types';


/** Element type of a monthly coupon response's `rateOutputs`. */
export type CouponRateOutput = NonNullable<PermitRateCouponOutput['rateOutputs']>[number];

// The API shouts its invalid-coupon message ("This coupon is NOT VALID/APPLICABLE");
// display it in sentence case per design (PB-4346). Other messages pass through as-is.
export const normalizeCouponErrorMessage = (message: string): string =>
  message.replace('NOT VALID/APPLICABLE', 'not valid/applicable');

/**
 * Resolves the coupon-adjusted rate output that should price the permit for the
 * selected rate card, given a monthly coupon response.
 *
 * Shared by the monthly details screen (`useMonthlyRateSelection`) and the monthly
 * payment screen (`useMonthlySignup`) so both derive the same effective rate.
 *
 * ## Invariants:
 * - A coupon that defines a special rate (`specialRate.documentId`) wins over the
 *   selected card's own coupon-adjusted output. Why: the special rate is the rate
 *   card the coupon is scoped to on the backend; ignoring it prices the permit at
 *   the wrong (non-special) rate.
 *
 * @returns the matching `rateOutputs` entry, or `null` when there is no coupon
 *          response or no match for the selection.
 */
export function resolveCouponRate(
  response: PermitRateCouponOutput | null | undefined,
  selectedRateCardId: string | null | undefined,
  preferSelected = false
): CouponRateOutput | null {
  const rateOutputs = response?.rateOutputs;
  if (!rateOutputs) return null;

  const specialRateMatch = response?.specialRate
    ? rateOutputs.find((r) => r.rateCardId === response.specialRate?.documentId)
    : null;

  const currentRateMatch = selectedRateCardId
    ? rateOutputs.find((r) => r.rateCardId === selectedRateCardId)
    : null;

  return preferSelected
    ? (currentRateMatch ?? specialRateMatch ?? null)
    : (specialRateMatch ?? currentRateMatch ?? null);
}

/**
 * Overlays coupon-adjusted rates from a monthly coupon response onto the rate
 * items already being displayed, matching by rate-card id.
 *
 * A flat-discount coupon discounts the *existing* rate cards (same `rateCardId`)
 * rather than swapping in a new special-rate card, so its coupon-adjusted price
 * only reaches the UI if the matching displayed item's `rate` is updated in
 * place. The callers still append genuinely-new coupon rate cards (the
 * special-rate-swap case, PB-3045) separately — this leaves those untouched and
 * only rewrites the price of cards that are already on screen (PB-3745).
 *
 * ## Invariants:
 * - Only overlays when the coupon output carries a numeric `rate` (>= 0). Why:
 *   a missing/negative rate means the coupon does not price that card, so the
 *   original rate must stand rather than being replaced with 0 or a bogus value.
 *
 * @param items       display rate items keyed by `id` (= `rateCardId ?? specialRateId`).
 * @param rateOutputs the coupon response's `rateOutputs`, or null/undefined.
 * @returns a new array in which matched items are shallow-copied with their
 *          `rate` overlaid and unmatched items are passed through by reference;
 *          the original `items` reference is returned unchanged when there is
 *          nothing to overlay.
 */
export function applyCouponRatesToItems<T extends { id: string; rate: number }>(
  items: T[],
  rateOutputs: CouponRateOutput[] | null | undefined
): T[] {
  if (!rateOutputs?.length) return items;

  const rateById = new Map<string, number>();
  for (const ro of rateOutputs) {
    const id = ro.rateCardId ?? ro.specialRateId ?? '';
    if (id && ro.rate != null && ro.rate >= 0) rateById.set(id, ro.rate);
  }
  if (rateById.size === 0) return items;

  return items.map((item) =>
    rateById.has(item.id) ? { ...item, rate: rateById.get(item.id) as number } : item
  );
}

/** The contract settings Sign and pay reads: which permit and autopay contracts the parker signs. */
export type RateContractSettings = Pick<
  GaragePermitRate,
  'customContract' | 'contractTemplateId' | 'customAutoPayAgreement' | 'autoPayTemplateId'
>;

/**
 * Which rate's contract settings apply once a coupon / rate-link response is in play (PB-8352).
 *
 * ## Invariants:
 * - The response's special rate wins outright when present — all four settings come from it,
 *   even with its Custom Contract off. Why: legacy picks the rate with
 *   `getPermitRateToUse = special || board` (MonthlySignUp / AddPermit), so a special rate without
 *   a custom contract signs the garage default, not the board rate's custom one.
 * - The coupon's rate outputs are never a source: they carry no contract fields, which is why a
 *   rate link used to fall back to the garage default contract.
 */
export function resolveCouponContractSettings(
  response: PermitRateCouponOutput | null | undefined,
  selectedRate: RateContractSettings
): RateContractSettings {
  const source = response?.specialRate ?? selectedRate;
  return {
    customContract: source.customContract,
    contractTemplateId: source.contractTemplateId,
    customAutoPayAgreement: source.customAutoPayAgreement,
    autoPayTemplateId: source.autoPayTemplateId,
  };
}

/**
 * The permit rate a selection refers to, whether or not the garage lists it.
 *
 * ## Invariants:
 * - A garage-listed rate always wins. Why: it carries fields the coupon output omits,
 *   so this must not change behaviour for any rate that already resolved.
 * - Otherwise falls back to the link/coupon response, the ONLY place a link-only rate
 *   exists: the V2 garage search lists BOARD_RATE cards alone
 *   (GarageServiceImpl.java:1070), so a shared rate link resolves to nothing without
 *   this and every downstream screen silently blanks (PB-5425). Legacy holds the same
 *   applyDiscount rate object as its selection (GarageListPage.tsx:3828-3831).
 * - `payUpfront` is remapped to `paymentUpfront`. Why: the generated coupon output and
 *   our hand-maintained `GaragePermitRate` disagree on the name, and the signup payload
 *   reads the latter — a plain spread drops it and quietly bills the wrong way.
 */
/**
 * The vehicle types a coupon or link rate is limited to (PB-8319).
 *
 * ## Invariants:
 * - Falls back to the response's `specialRate.vehicleTypes` when the matched rate output
 *   carries none, but only when that special rate IS the matched rate. Why: legacy reads the
 *   restriction from `monthlyCouponResponse.specialRate.vehicleTypes` (GarageListPage.tsx:999),
 *   and a rate output can arrive without it — dropping it lets a restricted rate be booked
 *   for any vehicle.
 *
 * @returns the restricting types, or `undefined` when the coupon adds no restriction.
 */
export function resolveCouponVehicleTypes(
  response: PermitRateCouponOutput | null | undefined,
  couponRate: CouponRateOutput | null | undefined
): string[] | undefined {
  if (couponRate?.vehicleTypes?.length) return couponRate.vehicleTypes;
  const specialRate = response?.specialRate;
  if (specialRate?.vehicleTypes?.length && specialRate.documentId === couponRate?.rateCardId) {
    return specialRate.vehicleTypes;
  }
  return undefined;
}

export function resolveSelectedPermitRate(
  permitRates: GaragePermitRate[] | null | undefined,
  rateCardId: string | null | undefined,
  response: PermitRateCouponOutput | null | undefined
): GaragePermitRate | null {
  const listed = (permitRates ?? []).find(
    (r) => (r.rateCardId ?? r.specialRateId ?? '') === rateCardId
  );
  if (listed) return listed;

  const linked = resolveCouponRate(response, rateCardId);
  if (!linked) return null;

  const { payUpfront, ...rest } = linked as CouponRateOutput & { payUpfront?: boolean };
  return { ...rest, paymentUpfront: payUpfront } as GaragePermitRate;
}
